import React, { useState, useEffect, useMemo } from 'react';
import {
  Wrapper,
  Container,
  Back,
  Line,
  Linha,
  Title,
  InfoContainer,
  ButtonContainer,
  Button,
  SubContainer,
} from './styles';

import { ReactComponent as IconCheck } from '../../assets/icons/alert.svg';

import GoBackPage from '../../components/GoBackPage';
import formatPrice from '../../services/utils/formatPrice';
import ModalContracts from '../../components/Modal/Contracts';

import { FiFileText } from 'react-icons/fi';
import { BiCalendar } from 'react-icons/bi';
import { useHistory, useLocation } from 'react-router-dom';
import { clearDealStatus } from '../../store/negotiation/actions';
import { useDispatch } from 'react-redux';
import { navigationUrl } from '../../store/analytic/actions';
import { page } from '../../store/auth/actions';
import { getContractsFromDealOrDebt } from '../../utils/getContractsToShow';
import getContractDescription from '../../utils/getContractDescription';
import BreadCrumb from '../../components/BreadCrumb';

type ProposalProps = {
  contract: GQL.IDebt;
  proposal: GQL.IProposalDetails;
  negotiation: GQL.IOption;
  status: string;
  from: string;
};

const PendingAndReject: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation<ProposalProps>();
  const from = location.state.from;
  const contract: GQL.IDebt = location.state && location.state.contract;
  const negotiationOption: GQL.IOption =
    location.state && location.state.negotiation;
  const status = location.state && location.state.status;
  const [open, setOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const contractsToShow = useMemo(() => getContractsFromDealOrDebt(contract), [
    contract,
  ]);

  const previousPages = useMemo(() => {
    if (from === 'negotiation') {
      return [
        { name: 'Meus débitos', route: '/contrato' },
        { name: 'Meu Débito', route: '/negociacao' },
        { name: 'Resumo', route: '/resumo-negociacao' },
        { name: 'Confirmação', route: '/confirmação-de-contatos' },
      ];
    } else {
      return [
        { name: 'Meus Acordos', route: '/acordos' },
        { name: 'Meu Acordo', route: '/parcela-acordo' },
      ];
    }
  }, [from]);

  const title = useMemo(
    () => (from === 'negotiation' ? 'Meu Débito' : 'Meu Acordo'),
    [from]
  );

  useEffect(() => {
    if (contract && negotiationOption) {
      dispatch(clearDealStatus());
      if (status === 'Pendente') {
        setPageTitle('Acordo Pendente');
        setDescription(
          'O acordo ainda não foi aprovado pelo credor! Entre em contato com a assessoria para maiores detalhes.'
        );
        dispatch(page('acordoPendente'));
        dispatch(
          navigationUrl.request({
            action: '/acordoPendente',
          })
        );
      } else {
        setPageTitle('Acordo Rejeitado');
        setDescription(
          'O acordo não foi aprovado pelo credor! Entre em contato com a assessoria para maiores detalhes ou para fazer um novo acordo!'
        );
        dispatch(page('acordoRejeitado'));
        dispatch(
          navigationUrl.request({
            action: '/acordoRejeitado',
          })
        );
      }
    } else {
      history.push('/contrato');
    }
  }, [contract, dispatch, history, negotiationOption, status]);

  /**
   * Posso chegar nesse componente vindo do fluxo de acordo[PortionAgreement(deal)] ou
   * vindo do fluxo de negociação de um débito[resumo-propost/steps de confirmação(negotiation)]
   *
   * O breadcrumb é relativo a de onde veio o usuário (deal | negotiation)
   *
   */

  return (
    <Wrapper>
      <Container>
        <Line>
          <Title>{title}</Title>

          <BreadCrumb
            previouPages={previousPages}
            showBreadCrumbOnMinWidth='960px'
            currentPage={pageTitle}
          />

          <Back>
            <GoBackPage text='Meu Débito' />
          </Back>
        </Line>
        <SubContainer>
          <IconCheck />
          <div className='card-subcontainer'>
            <p className='title-subcontainer'>{pageTitle}</p>
            <span className='desc-subcontainer'>{description}</span>
          </div>
        </SubContainer>
        <Linha />
        <InfoContainer>
          <span className='title-infocontainer'>resumo do acordo</span>
          <h2>
            {location.state.from === 'deal'
              ? 'Valor do acordo: R$ '
              : 'Valor da dívida: R$ '}
            {formatPrice(contract && contract.updatedValue)}
          </h2>
          <div className='value'>
            <span className='spotlight'>
              Pagamento {negotiationOption.plan === 1 ? '' : 'em '}
              <p className='strong'>
                {negotiationOption.plan === 1
                  ? 'à vista'
                  : `${negotiationOption.plan} parcelas`}
              </p>
              {negotiationOption.plan === 1 ? '(parcela única)' : ''}
            </span>
          </div>
          <p className='credor'>{contract && contract.creditorName}</p>
          <div className='options'>
            <FiFileText className='space-right' />
            {contract && contract.contracts.length > 1
              ? 'Múltiplos contratos: '
              : 'Contrato: '}
            {contract && contract.contracts.length === 1 && (
              <strong>{contract && contract.contracts[0]}</strong>
            )}
            {contract && contract.contracts.length > 1 && (
              <a onClick={() => setOpen(true)}>Clique para exibir</a>
            )}
          </div>
          <div className='options'>
            <BiCalendar className='space-right' />
            Vencimento da entrada:{' '}
            <span className='date'>{negotiationOption.formatedDueDate}</span>
          </div>
          {contract && contract.contracts.length !== 1 && (
            <span
              className='options'
              style={{ marginBottom: 0, wordBreak: 'break-word' }}
            >
              {getContractDescription(contractsToShow[0])}
            </span>
          )}
        </InfoContainer>
        <ButtonContainer>
          <Button color='secondary' onClick={() => history.goBack()}>
            Voltar
          </Button>
        </ButtonContainer>
      </Container>
      {open && (
        <ModalContracts
          handlerClose={() => setOpen(false)}
          contracts={contractsToShow}
        />
      )}
    </Wrapper>
  );
};

export default PendingAndReject;
